/**
 * The wormhole, drawn as it actually happens.
 *
 * Every block this observer decodes reports how many notes it committed and
 * how many an exit released (`ServerMessage::Wormhole`). A note arriving falls
 * inward and is swallowed; a note leaving climbs out and escapes. Nothing here
 * is on a timer or a loop — **a still stage means a still chain**, and that is
 * information rather than a bug.
 *
 * Three things keep it honest:
 *
 * - **Particles are capped and the caption is not.** A block committing 900
 *   notes draws `MAX_PARTICLES` of them and says nine hundred in words. Drawing
 *   one mark per note would melt the tab and still be uncountable, and a
 *   picture nobody can count is decoration — so the number lives in the text,
 *   where it is exact.
 * - **Direction is the same encoding the chart uses**: inward against outward,
 *   never two hues. This site has one data colour and the two-shade
 *   alternative fails CVD separation, so a note's direction is its travel.
 * - **It is never the only way to read this.** The chart and the tables below
 *   carry every figure; this is the same events with the latency removed.
 *
 * Rendered by mutating pooled SVG nodes from one `requestAnimationFrame` loop
 * rather than through React state. Sixty state updates a second across a tree
 * this size is the cost React is bad at, and none of it is worth a re-render.
 */

import { useEffect, useMemo, useRef, useState } from 'react'

import { Link } from 'react-router-dom'

import { fetchWormhole } from '../api/rest'
import type { RecentBlock } from '../api/generated/RecentBlock'
import type { WormholePulse } from '../api/socket'
import { shortAddress, tokens } from '../lib/format'
import { href } from '../lib/routes'
import { useObserverInstance } from '../lib/observer-context'

const HEIGHT = 210
const FALLBACK_WIDTH = 720
/**
 * Most marks alive at once.
 *
 * A ceiling on work, not a claim about the chain: mainnet commits a few
 * thousand notes on a busy day and a single block has carried hundreds. The
 * caption always says the real count.
 */
const MAX_PARTICLES = 96
/**
 * How long one note takes to fall in or climb out.
 *
 * Measured against mainnet rather than guessed: the chain commits about 1.9
 * notes a block and settles an exit roughly every fifth block, so at the
 * original 1.5s a mark was almost always alone and usually absent. Five
 * seconds against a twelve-second block is long enough that the few notes
 * there are overlap, without a note still falling when its successor arrives.
 */
const FLIGHT_MS = 5000

/**
 * Most marks drawn for the notes already inside.
 *
 * The standing population is the headline claim of this whole page — tens of
 * thousands of notes committed and not yet spent — so one mark is a sample
 * standing for many, and the caption always says how many. Three shells share
 * these so the ring does not rotate as one rigid disc.
 */
const STOCK_MARKS = 45
const STOCK_SHELLS = 3
/** How often the standing population is re-read. It moves slowly; this is not a feed. */
const STOCK_REFRESH_MS = 120_000

/**
 * Lines kept in the console.
 *
 * The stage's marks are not self-explanatory — a dot travelling inward could be
 * anybody paying anybody — so each one gets a line saying what it was. Ten is
 * about half a minute of mainnet, which is long enough to read and short enough
 * that the oldest line is still recent.
 */
const CONSOLE_LINES = 12

/**
 * Lines any one block may contribute.
 *
 * Without it a single busy block fills the console and the reader loses all
 * sense of rate — one mainnet block has carried hundreds of notes, and twelve
 * lines from it look identical to twelve blocks of one. Four keeps several
 * blocks on screen, and the count of what is not shown sits on the block's own
 * first line.
 */
const CONSOLE_LINES_PER_BLOCK = 4

/**
 * How old the snapshot's block may be said to be before the figure is refused.
 *
 * The only seed available on load is the difference between two machines'
 * clocks, so it is believed at all only while it stays plausible. Generous
 * against a slow chain, tight enough that gross skew shows the plain text
 * instead of a number nobody should read.
 */
const SEED_LIMIT_MS = 120_000
/** Most marks any single block may spawn, so one big block cannot fill the pool. */
const PER_PULSE_CAP = 28

/**
 * What each kind of arrival is, in words a reader has not had to look up.
 *
 * The glyph repeats the direction the mark travelled, so a line and the dot it
 * describes can be matched without reading the label — the same
 * position-over-colour reasoning the flow chart uses.
 */
const KINDS: Record<string, { glyph: string; label: string }> = {
  reward: { glyph: '\u2193', label: 'mined' },
  transfer: { glyph: '\u2193', label: 'sent' },
  exit: { glyph: '\u2191', label: 'exit' },
}

interface ConsoleLine {
  /**
   * Unique per line, from a counter rather than from the block.
   *
   * Keying on `height-index` looked unique and was not: the same height can
   * arrive twice — a reorg re-records it, and for one deploy the backfill
   * re-announced it — and React given duplicate keys renders duplicates *and*
   * stops honouring the list's own length cap. The console grew past twelve
   * lines showing one block three times. A counter cannot collide.
   */
  id: number
  height: number
  kind: string
  to: string
  amount: string
  /** Notes this block carried that got no line of their own. */
  elided: number
}

interface Particle {
  /** Set while in flight; a free slot is `null`. */
  born: number
  out: boolean
  /** Where it crosses the rim, in radians. */
  angle: number
  /** Start (or end) distance from the mouth, in px. */
  reach: number
  /** 0.6–1 — a larger note draws a larger mark. */
  scale: number
}

function useMeasuredWidth(): [React.RefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(FALLBACK_WIDTH)
  useEffect(() => {
    const node = ref.current
    if (!node) return
    const observe = new ResizeObserver(([entry]) => {
      if (entry) setWidth(Math.max(280, entry.contentRect.width))
    })
    observe.observe(node)
    return () => observe.disconnect()
  }, [])
  return [ref, width]
}

/** `true` when the viewer has asked for less motion, and whenever that changes. */
function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false)
  useEffect(() => {
    // `matchMedia` is absent in some embedded viewers; absent means no stated
    // preference, which is the same as not having asked for less motion.
    if (typeof window.matchMedia !== 'function') return
    const query = window.matchMedia('(prefers-reduced-motion: reduce)')
    setReduced(query.matches)
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches)
    query.addEventListener('change', onChange)
    return () => query.removeEventListener('change', onChange)
  }, [])
  return reduced
}

/** Eased 0→1. Slow at the rim, quick at the mouth: a fall, not a slide. */
function ease(t: number): number {
  return t * t * (3 - 2 * t)
}

/**
 * A block height, as a link to it where there is a chain to link into.
 *
 * Falls back to plain text rather than a dead link when the chain is not yet
 * known: `href` would render `//block/43466`, which looks clickable and goes
 * nowhere. The number is the same either way, so nothing is lost.
 */
function BlockLink({ chain, height }: { chain: string | null; height: number }) {
  const label = `#${height.toLocaleString('en-US')}`
  if (chain === null) return <>{label}</>
  return <Link to={href({ chain, block: String(height) })}>{label}</Link>
}

export function WormholeStage({
  chain,
  lastBlock,
  decimals,
  symbol,
}: {
  chain: string | null
  /** The newest block in the ticker, for the waiting counter. */
  lastBlock: RecentBlock | null
  decimals: number
  symbol: string
}) {
  const observer = useObserverInstance()
  const [wrap, width] = useMeasuredWidth()
  const reduced = useReducedMotion()
  const [last, setLast] = useState<WormholePulse | null>(null)
  /**
   * Notes committed and not yet spent.
   *
   * Read from the chain, never accumulated from pulses. A pulse's `out` is an
   * exit *crediting* an account, which inserts a fresh leaf of its own — what
   * actually retires a note is a nullifier, and those are not counted per
   * block. Adding and subtracting pulses here would drift away from the truth
   * and look authoritative doing it.
   */
  const [outstanding, setOutstanding] = useState<number | null>(null)
  /** Newest first, the way the block ticker reads. Oldest falls off the end. */
  const [log, setLog] = useState<ConsoleLine[]>([])
  /** Monotonic line id. See `ConsoleLine.id` for why this is not derived. */
  const nextLineId = useRef(0)

  /**
   * Where the waiting counter counts from: a monotonic mark, plus however long
   * the block was already old when we took it.
   *
   * The ticking is always `performance.now()`, which cannot be skewed or
   * stepped by the system clock. Only the *seed* can be inexact, and only for
   * the block the snapshot hands over on load — for that one the sole evidence
   * of its age is `observed_at`, the server's wall clock, so the seed carries
   * whatever the two machines disagree by. Every block that arrives afterwards
   * seeds at zero and is exact.
   *
   * The first cut refused to seed at all and waited for a live arrival. That
   * was more honest and completely useless: on mainnet the block that triggers
   * it is the same block that puts the first row in the console, so the counter
   * became visible and unnecessary in the same tick and **was never once
   * seen**. Reported from the live site after a refresh.
   */
  const [anchor, setAnchor] = useState<{ at: number; offsetMs: number; height: number } | null>(
    null,
  )
  const lastHeight = useRef<number | null>(null)
  useEffect(() => {
    if (lastBlock === null) return
    if (lastBlock.height === lastHeight.current) return
    const first = lastHeight.current === null
    lastHeight.current = lastBlock.height

    if (!first) {
      setAnchor({ at: performance.now(), offsetMs: 0, height: lastBlock.height })
      return
    }

    // The snapshot's newest block, whose age only the server can tell us.
    // Clamped rather than trusted: a clock disagreeing by more than a couple of
    // minutes would show a wild figure or count backwards, and no counter beats
    // a wrong one.
    const seed = Date.now() - Date.parse(lastBlock.observed_at)
    if (Number.isFinite(seed) && seed >= 0 && seed <= SEED_LIMIT_MS) {
      setAnchor({ at: performance.now(), offsetMs: seed, height: lastBlock.height })
    }
  }, [lastBlock])

  /**
   * The waiting counter, written straight to the DOM.
   *
   * A number changing sixty times a second through React state would re-render
   * this component — the mouth, the population, the whole console — for one
   * span of text. Same reasoning as the flight marks.
   */
  const elapsed = useRef<HTMLSpanElement>(null)
  useEffect(() => {
    if (anchor === null || log.length > 0) return
    let frame = 0
    let timer = 0
    const paint = () => {
      if (elapsed.current) {
        const ms = Math.max(0, Math.round(anchor.offsetMs + performance.now() - anchor.at))
        elapsed.current.textContent = ms.toLocaleString('en-US')
      }
    }
    if (reduced) {
      // Still a counter, at a quarter of the flicker.
      paint()
      timer = window.setInterval(paint, 250)
    } else {
      const tick = () => {
        paint()
        frame = requestAnimationFrame(tick)
      }
      frame = requestAnimationFrame(tick)
    }
    return () => {
      if (frame) cancelAnimationFrame(frame)
      if (timer) window.clearInterval(timer)
    }
  }, [anchor, log.length, reduced])

  useEffect(() => {
    if (chain === null) return
    const controller = new AbortController()
    const read = () => {
      fetchWormhole(chain, controller.signal)
        .then((w) => {
          if (w.notes_created === null || w.notes_spent === null) return
          setOutstanding(Math.max(0, w.notes_created - w.notes_spent))
        })
        .catch(() => {
          // The stage is decoration over a figure that is on the page below it
          // anyway. A failed read leaves the mouth empty rather than an error.
        })
    }
    read()
    const timer = window.setInterval(read, STOCK_REFRESH_MS)
    return () => {
      controller.abort()
      window.clearInterval(timer)
    }
  }, [chain])

  const pool = useRef<(SVGCircleElement | null)[]>([])
  const particles = useRef<Particle[]>(
    Array.from({ length: MAX_PARTICLES }, () => ({
      born: 0,
      out: false,
      angle: 0,
      reach: 0,
      scale: 1,
    })),
  )
  /** Which slots are in flight. A slot is reusable the moment its note lands. */
  const live = useRef<Set<number>>(new Set())
  /** Whether a frame is already scheduled, so a burst of pulses starts one loop. */
  const running = useRef(false)
  const mounted = useRef(true)
  const geometry = useRef({
    cx: FALLBACK_WIDTH / 2,
    cy: HEIGHT / 2,
    rim: 150,
    rx: 100,
    squash: 0.42,
  })

  // The mouth scales with the panel. Fixed radii left a 1200px-wide stage mostly
  // empty with a small ellipse adrift in it, and pushed the rim so far out that
  // notes spent most of their flight off-canvas — visible only for the last
  // moment before they landed, which is the half that reads as nothing
  // happening.
  const rx = Math.min(width * 0.3, 300)
  // How flat the mouth is. Capped so the outermost ring fits the stage rather
  // than being cut off top and bottom, and reused as the squash on every note's
  // path — the marks have to travel in the plane of the thing they are falling
  // into, or they read as passing in front of it.
  const ry = Math.min(rx * 0.42, HEIGHT / 2 - 12)
  const geo = {
    cx: width / 2,
    cy: HEIGHT / 2,
    // Just past the mouth rather than past the panel: a note has to be on
    // screen for its whole travel or the motion is not the thing being read.
    rim: rx * 1.5,
    rx,
    squash: ry / rx,
  }
  geometry.current = geo

  useEffect(() => {
    mounted.current = true
    // Captured rather than read in the cleanup: the `Set` is created once by
    // `useRef` and never replaced, so this is the same object either way — but
    // holding it locally is what makes that promise visible, here and to the
    // exhaustive-deps rule.
    const inFlight = live.current
    return () => {
      mounted.current = false
      inFlight.clear()
      running.current = false
    }
  }, [])

  /**
   * Advance every mark in flight by one frame.
   *
   * Held in a ref and reassigned each render so the loop always sees the
   * current geometry without being torn down and restarted on every resize.
   */
  const step = useRef<() => void>(() => {})
  step.current = () => {
    const now = performance.now()
    const { cx: x0, cy: y0, squash } = geometry.current
    for (const slot of Array.from(live.current)) {
      const p = particles.current[slot]!
      const node = pool.current[slot]
      const age = now - p.born
      // Staggered: a pulse's notes are dealt out over a few frames rather than
      // arriving as one ring, which reads as a burst instead of a shockwave.
      if (age < 0) continue
      if (age > FLIGHT_MS) {
        live.current.delete(slot)
        node?.setAttribute('opacity', '0')
        continue
      }
      const t = ease(age / FLIGHT_MS)
      // In falls from the rim to the mouth; out climbs the other way.
      const distance = p.out ? p.reach * t : p.reach * (1 - t)
      // A little swirl, so it reads as a throat rather than a drain pipe.
      const angle = p.angle + (p.out ? -1 : 1) * t * 0.9
      if (!node) continue
      node.setAttribute('cx', String(x0 + Math.cos(angle) * distance))
      // Flattened vertically to match the ellipses: the mouth is seen at an
      // angle, so a note's path has to lie in the same plane.
      node.setAttribute('cy', String(y0 + Math.sin(angle) * distance * squash))
      node.setAttribute('r', String(1.6 + p.scale * 2.2 * (p.out ? t : 1 - t)))
      // Fades at the far end either way: arriving notes vanish into the mouth,
      // leaving ones dissolve into the rest of the chain.
      node.setAttribute('opacity', String(p.out ? 1 - t * t : Math.min(1, (1 - t) * 2.4)))
    }
  }

  /**
   * Run frames until nothing is in flight, then stop completely.
   *
   * A still chain schedules no frames at all — the loop is started by a pulse
   * and ends itself. A page left open on a quiet chain costs nothing, which an
   * always-on `requestAnimationFrame` would not.
   */
  const start = useRef<() => void>(() => {})
  start.current = () => {
    if (running.current || live.current.size === 0) return
    running.current = true
    const frame = () => {
      if (!mounted.current) {
        running.current = false
        return
      }
      step.current()
      if (live.current.size > 0) {
        requestAnimationFrame(frame)
      } else {
        running.current = false
      }
    }
    requestAnimationFrame(frame)
  }

  // Subscribe to pulses. Deliberately outside React state: `setLast` is the
  // only thing that re-renders, and it carries the caption, not the motion.
  useEffect(() => {
    return observer.onWormhole((pulse: WormholePulse) => {
      setLast(pulse)

      const spawn = (count: number, out: boolean, total: string) => {
        const draw = Math.min(count, PER_PULSE_CAP)
        if (draw === 0) return
        // Mean value of the notes this pulse stands in for, as a fraction of a
        // whole token, so a block moving thousands draws heavier marks than one
        // moving change. Crude on purpose: it is a size, not a figure.
        let mean = 0
        try {
          mean = Number(BigInt(total) / BigInt(count)) / 10 ** decimals
        } catch {
          mean = 0
        }
        const weight = Math.min(1, Math.max(0, Math.log10(Math.max(mean, 0.01)) + 2) / 4)
        for (let i = 0; i < draw; i += 1) {
          let slot = -1
          for (let s = 0; s < MAX_PARTICLES; s += 1) {
            if (!live.current.has(s)) {
              slot = s
              break
            }
          }
          // Pool exhausted: this note is not drawn. The caption still counts it.
          if (slot === -1) return
          live.current.add(slot)
          const p = particles.current[slot]!
          p.born = performance.now() + i * 45
          p.out = out
          p.angle = Math.random() * Math.PI * 2
          p.reach = geometry.current.rim * (0.75 + Math.random() * 0.35)
          p.scale = 0.6 + weight * 0.4
        }
      }

      // The console is fed whether or not the marks are, so a viewer who has
      // asked for less motion still gets the reading.
      const total = pulse.notesIn + pulse.notesOut
      const shown = pulse.notes.slice(0, CONSOLE_LINES_PER_BLOCK)
      const elided = Math.max(0, total - shown.length)
      setLog((previous) =>
        [
          ...shown.map((n, i) => ({
            id: nextLineId.current++,
            height: pulse.height,
            kind: n.kind,
            to: n.to,
            amount: n.amount,
            // On the block's *first* line, which is the one that survives: the
            // list is newest-first, so a count hung on the last line would be
            // the first thing trimmed by `CONSOLE_LINES` and the reader would
            // never learn a block of 260 showed them four.
            elided: i === 0 ? elided : 0,
          })),
          ...previous,
        ].slice(0, CONSOLE_LINES),
      )

      if (reduced) return
      spawn(pulse.notesIn, false, pulse.amountIn)
      spawn(pulse.notesOut, true, pulse.amountOut)
      start.current()
    })
  }, [observer, reduced, decimals])

  /**
   * The held notes, placed once per (population, size) rather than per frame.
   *
   * Each shell is a ring of marks on a *circle*, drawn inside a group that is
   * squashed vertically — so a plain CSS rotation of the ring traces an ellipse
   * in the mouth's own plane. That is what lets the standing population turn
   * forever without a `requestAnimationFrame` running: three composited
   * rotations cost nothing, where a JS loop on a page left open all day would
   * not. Shells turn at different rates and alternate direction so the cloud
   * does not read as one rigid disc.
   */
  const stock = useMemo(() => {
    if (outstanding === null || outstanding === 0) return null
    const drawn = Math.min(STOCK_MARKS, outstanding)
    const shells = []
    for (let si = 0; si < STOCK_SHELLS; si += 1) {
      const count = Math.floor(drawn / STOCK_SHELLS) + (si < drawn % STOCK_SHELLS ? 1 : 0)
      if (count === 0) continue
      const radius = geo.rx * (0.4 + si * 0.2)
      const marks = Array.from({ length: count }, (_, mi) => {
        // Evenly spaced, then nudged by an offset derived from the indices, so
        // the ring is not a clock face and is still the same every render.
        const angle = (mi / count) * Math.PI * 2 + ((si * 7 + mi * 3) % 11) * 0.06
        return {
          x: Math.cos(angle) * radius,
          y: Math.sin(angle) * radius,
          r: 1.5 + ((mi + si) % 3) * 0.35,
        }
      })
      shells.push({ marks, seconds: 120 + si * 45, reverse: si % 2 === 1 })
    }
    return { shells, drawn, per: Math.round(outstanding / drawn) }
  }, [outstanding, geo.rx])

  const { cx, cy } = geo

  return (
    <div className="stage-wrap">
      {/* The ref is on the figure rather than the panel: in the two-column
          layout the SVG's width is its *column's* width, and measuring the
          panel would size the mouth for space the console is occupying. */}
      <div ref={wrap} className="stage-figure">
        <svg
          className="chart stage"
          width={width}
          height={HEIGHT}
          viewBox={`0 0 ${width} ${HEIGHT}`}
          role="img"
          aria-label={[
            outstanding === null
              ? 'The wormhole.'
              : `The wormhole, holding ${outstanding} notes committed and not yet spent.`,
            last === null
              ? 'Waiting for the next block.'
              : `Block ${last.height} committed ${last.notesIn} notes and released ${last.notesOut}.`,
          ].join(' ')}
        >
          {/* The mouth. Three ellipses of the one hue at descending wash weight —
            a throat seen edge-on, not a second series. */}
          <ellipse className="stage-rim stage-rim-3" cx={cx} cy={cy} rx={rx} ry={ry} />
          <ellipse
            className="stage-rim stage-rim-2"
            cx={cx}
            cy={cy}
            rx={rx * 0.64}
            ry={ry * 0.64}
          />
          <ellipse
            className="stage-rim stage-rim-1"
            cx={cx}
            cy={cy}
            rx={rx * 0.33}
            ry={ry * 0.33}
          />
          <ellipse className="stage-core" cx={cx} cy={cy} rx={rx * 0.11} ry={ry * 0.11} />

          {/* The notes already inside, turning slowly. Drawn before the marks in
            flight so an arriving note passes in front of the population it is
            about to join. */}
          {stock?.shells.map((shell, si) => (
            <g key={si} transform={`translate(${cx} ${cy}) scale(1 ${geo.squash})`}>
              <g
                className="stage-shell"
                style={{
                  animationDuration: `${shell.seconds}s`,
                  animationDirection: shell.reverse ? 'reverse' : 'normal',
                }}
              >
                {shell.marks.map((m, mi) => (
                  <circle className="stage-held" key={mi} cx={m.x} cy={m.y} r={m.r} />
                ))}
              </g>
            </g>
          ))}

          {particles.current.map((_, i) => (
            <circle
              key={i}
              className="stage-note"
              ref={(node) => {
                pool.current[i] = node
              }}
              cx={cx}
              cy={cy}
              r={2}
              opacity={0}
            />
          ))}
        </svg>

        <div className="stage-caption">
          {last === null ? (
            // The caption is about wormhole activity, the console's counter about
            // blocks. Saying "watching for the next block" here while the counter
            // beside it times one the page has plainly seen reads as a
            // contradiction; this says which of the two is still missing.
            <span className="stage-idle">Nothing has moved through yet.</span>
          ) : (
            <>
              <strong>#{last.height.toLocaleString('en-US')}</strong>
              <span>
                {last.notesIn.toLocaleString('en-US')} in
                {last.notesIn > 0 && ` · ${tokens(last.amountIn, decimals, 1)} ${symbol}`}
              </span>
              <span>
                {last.notesOut.toLocaleString('en-US')} out
                {last.notesOut > 0 && ` · ${tokens(last.amountOut, decimals, 1)} ${symbol}`}
              </span>
            </>
          )}
          {/* Outside the block branch on purpose: the held population is drawn
            from the moment the page loads, and a reader arriving between blocks
            would otherwise see forty-five turning marks with nothing saying
            what they are. */}
          {stock !== null && outstanding !== null && (
            <span className="stage-idle">
              {outstanding.toLocaleString('en-US')} notes held
              {stock.per > 1 && ` · one mark per ${stock.per.toLocaleString('en-US')}`}
            </span>
          )}
          {reduced && <span className="stage-idle">motion off</span>}
        </div>
      </div>

      {/* What the marks were. The stage on its own is a dot travelling inward,
          which could be anybody paying anybody; this is the half that makes it
          a reading of the chain rather than an ornament. Newest at the top, the
          way the block ticker reads — which also means no auto-scrolling to
          fight with, and the line a reader is looking for never moves. */}
      <ol className="stage-log" aria-label="Recent notes through the wormhole">
        {log.length === 0 ? (
          <li className="stage-log-empty">
            {anchor === null ? (
              'Each note a block commits or releases will be listed here as it happens.'
            ) : (
              <>
                <span ref={elapsed} className="stage-elapsed">
                  0
                </span>{' '}
                ms since <BlockLink chain={chain} height={anchor.height} />
              </>
            )}
          </li>
        ) : (
          log.map((line, i) => {
            const kind = KINDS[line.kind] ?? { glyph: '·', label: line.kind }
            // A block's other lines leave the height column blank, so a run of
            // notes reads as one block rather than as a column of repetition.
            const repeat = i > 0 && log[i - 1]?.height === line.height
            return (
              <li key={line.id}>
                <span className="stage-log-height">
                  {repeat ? '' : <BlockLink chain={chain} height={line.height} />}
                </span>
                <span className={`stage-log-kind stage-log-${line.kind}`}>
                  {kind.glyph} {kind.label}
                </span>
                <span className="stage-log-amount">
                  {tokens(line.amount, decimals, 4)} {symbol}
                </span>
                <span className="stage-log-to">
                  {chain === null ? (
                    shortAddress(line.to)
                  ) : (
                    <Link to={href({ chain, account: line.to })} title={line.to}>
                      {shortAddress(line.to)}
                    </Link>
                  )}
                </span>
                {line.elided > 0 && (
                  <span className="stage-idle stage-log-more">
                    +{line.elided.toLocaleString('en-US')} more in{' '}
                    <BlockLink chain={chain} height={line.height} />
                  </span>
                )}
              </li>
            )
          })
        )}
      </ol>
    </div>
  )
}
